import React, { useEffect, useRef, useState } from 'react';
import './Home.css';

function FeatureCard({ icon, title, desc, delay }) {
  return (
    <div className="feature-card" style={{ animationDelay: `${delay}ms` }}>
      <div className="feature-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function StatItem({ value, label }) {
  return (
    <div className="stat-item">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

export default function Home({ navigate }) {
  const [visible, setVisible] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
  }, []);

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb orb-1" />
          <div className="hero-orb orb-2" />
          <div className="hero-orb orb-3" />
          <div className="hero-grid" />
        </div>

        <div className="container hero-content">
          <div className={`hero-text ${visible ? 'visible' : ''}`}>
            <div className="hero-badge">
              <span className="badge-dot" />
              Now Live in Australia 🇦🇺
            </div>
            <h1>
              Enjoy Your Night with<br />
              <span className="gradient-text">HiveeClub</span>
            </h1>
            <p>
              The ultimate social and entertainment platform for discovering events, buying tickets, and connecting with people around you.
              Available now on the App Store and Google Play.
            </p>
            <div className="hero-actions">
              <a href="https://apps.apple.com/au/app/hiveeclub/id1584271794" target="_blank" rel="noopener noreferrer" className="store-btn apple">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                <div>
                  <small>Download on the</small>
                  <strong>App Store</strong>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.hivee.hivee" target="_blank" rel="noopener noreferrer" className="store-btn google">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c.33.18.7.24 1.08.17l12.29-7.08-2.6-2.6-10.77 9.51zm-1.06-1.38V1.62L13.64 12 2.12 22.38zm17.3-11.43L16.8 9.4 14 12l2.8 2.8 2.66-1.54a1.5 1.5 0 0 0 0-2.52zM4.26.07L16.55 7.15 13.95 9.75 3.18.24A1.5 1.5 0 0 0 4.26.07z"/></svg>
                <div>
                  <small>Get it on</small>
                  <strong>Google Play</strong>
                </div>
              </a>
            </div>
          </div>

          <div className={`hero-phone ${visible ? 'visible' : ''}`}>
            <div className="phone-mockup">
              <div className="phone-frame">
                <div className="phone-screen">
                  <img src="/app-screenshot.png" alt="HiveeClub App" className="phone-screenshot-img" />
                </div>
              </div>
              <div className="phone-glow" />
            </div>
          </div>
        </div>

        <div className="hero-stats">
          <div className="container stats-row">
            <StatItem value="10K+" label="Active Users" />
            <div className="stat-divider" />
            <StatItem value="200+" label="Events Hosted" />
            <div className="stat-divider" />
            <StatItem value="50K+" label="Tickets Sold" />
            <div className="stat-divider" />
            <StatItem value="4.8★" label="Average Rating" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Everything You Need<br /><span className="gradient-text">For a Perfect Night Out</span></h2>
            <p>HiveeClub brings together social networking, event ticketing, and nightlife entertainment in one seamless platform.</p>
          </div>
          <div className="features-grid">
            <FeatureCard
              delay={0}
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 12c0-1.1-.9-2-2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.1 0 2-.9 2-2zm-2 4H4V6h12v10h2v-4h-2v4zm-4-8H8v2h6V8zm-6 4h4v2H8v-2z"/>
                </svg>
              }
              title="Book Tickets & VIP Tables"
              desc="Book tickets, reserve VIP tables, and plan birthday celebrations — all in one place. Instant QR code entry means less waiting and a smoother experience."
            />
            <FeatureCard
              delay={100}
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
                </svg>
              }
              title="A Social Platform for Nightlife"
              desc="Join live group chats and private conversations with other event-goers. Automatically access event group chats when you check in and connect with people around you."
            />
          </div>
        </div>
      </section>

      {/* Tickets Section */}
      <section className="detail-section">
        <div className="container detail-grid">
          <div className="detail-visual left">
            <div className="ticket-card-demo">
              <div className="ticket-header">
                <div className="ticket-type">VIP TABLE</div>
                <div className="ticket-dots">⬡⬡⬡</div>
              </div>
              <div className="ticket-event">WAO Superclub</div>
              <div className="ticket-date">Friday, 29 May 2026</div>
              <div className="ticket-divider">
                <div className="ticket-hole left-hole" />
                <div className="ticket-dashes" />
                <div className="ticket-hole right-hole" />
              </div>
              <div className="ticket-qr">
                <div className="qr-placeholder">
                  <img src="/ticket-qr.png" alt="Ticket QR Code" className="ticket-qr-img" onError={(e) => {
                    e.target.style.display='none';
                    e.target.nextSibling.style.display='flex';
                  }} />
                  <div className="qr-grid" style={{display:'none'}}>
                    {Array.from({length:25}).map((_,i) => (
                      <div key={i} className={`qr-cell ${Math.random()>0.5?'filled':''}`} />
                    ))}
                  </div>
                </div>
                <div className="qr-info">
                  <strong>Scan to Enter</strong>
                  <span>#HVC-2026-A86F</span>
                </div>
              </div>
            </div>
          </div>
          <div className="detail-content">
            <span className="section-tag">Ticketing</span>
            <h2>Book Tickets & <span className="gradient-text">VIP Tables</span></h2>
            <p>
              Discover and book nightlife events in seconds.
              From event tickets to VIP tables and birthday packages, everything is designed to be fast and effortless.
            </p>
            <ul className="feature-list">
              <li>
                <span className="check">✓</span>
                Auto-generated QR code for instant entry
              </li>
              <li>
                <span className="check">✓</span>
                No more waiting in line for tickets
              </li>
              <li>
                <span className="check">✓</span>
                Birthday parties and group bookings supported
              </li>
              <li>
                <span className="check">✓</span>
                Real-time availability and instant confirmation
              </li>
            </ul>
            <a href="https://apps.apple.com/au/app/hiveeclub/id1584271794" target="_blank" rel="noopener noreferrer" className="btn-primary">
              Get Started Today
            </a>
          </div>
        </div>
      </section>

      {/* Social Section */}
      <section className="detail-section alt">
        <div className="container detail-grid reverse">
          <div className="detail-content">
            <span className="section-tag">Social</span>
            <h2>A Social Platform <span className="gradient-text">for Nightlife</span></h2>
            <p>
              HiveeClub is more than just ticketing — it's a social space where you can connect with people,
              share moments, and discover nightlife experiences together.
            </p>
            <ul className="feature-list">
              <li>
                <span className="check">✓</span>
                Add friends and grow your nightlife circle
              </li>
              <li>
                <span className="check">✓</span>
                Share photos and memories from events
              </li>
              <li>
                <span className="check">✓</span>
                Explore profiles and connect with other users
              </li>
              <li>
                <span className="check">✓</span>
                Stay connected before and after every night out
              </li>
            </ul>
            <a href="https://play.google.com/store/apps/details?id=com.hivee.hivee" target="_blank" rel="noopener noreferrer" className="btn-primary">
              Join the Community
            </a>
          </div>
          <div className="detail-visual right">
            <div className="chat-demo">
              <div className="chat-header">
                <div className="chat-avatar">👥</div>
                <div>
                  <div className="chat-title">Your Nightlife Circle</div>
                  <div className="chat-sub">Connect with people you meet</div>
                </div>
              </div>
              <div className="chat-messages">
                <div className="msg msg-other">
                  <div className="msg-avatar">A</div>
                  <div className="msg-bubble">Just added you from WAO last night! 🎉</div>
                </div>
                <div className="msg msg-other">
                  <div className="msg-avatar">K</div>
                  <div className="msg-bubble">See you at the next event! 🔥</div>
                </div>
                <div className="msg msg-self">
                  <div className="msg-bubble">Already got my tickets 🎟️</div>
                </div>
                <div className="msg msg-other">
                  <div className="msg-avatar">M</div>
                  <div className="msg-bubble">Let's go together! 🙌</div>
                </div>
              </div>
              <div className="chat-input">
                <span>Message a friend...</span>
                <div className="send-btn">➤</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section className="rewards-section">
        <div className="container">
          <div className="section-header">
            <h2>Loyalty <span className="gradient-text">Rewards</span></h2>
            <p>Earn points every time you attend, redeem exclusive rewards, and level up your tier.</p>
          </div>
          <div className="tiers-grid">
            {[
              { name: 'Gold', color: '#f5c842', gemColors: ['#FFE066','#D4A017','#FFF3A0'], desc: 'Sign up and start earning rewards instantly.', perks: ['10 points on signup','1 point per A$1 spent','Birthday +100 points'] },
              { name: 'Platinum', color: '#b89fff', gemColors: ['#D4BFFF','#8B5FD4','#EDE0FF'], desc: 'Spend $3,000+ within 12 months to unlock.', perks: ['All Gold benefits','Exclusive Platinum events','Priority booking access'] },
              { name: 'Diamond', color: '#60c0ff', gemColors: ['#7DD3FC','#0EA5E9','#BAE8FF'], desc: 'Spend $6,000+ within 12 months to unlock.', perks: ['All Platinum benefits','Diamond VIP treatment','Exclusive member gifts'] },
            ].map(tier => (
              <div key={tier.name} className="tier-card" style={{ '--tier-color': tier.color }}>
                <div className="tier-icon">
                  <svg viewBox="0 0 60 52" width="52" height="52" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Crown top-left facet */}
                    <polygon points="14,3 30,3 22,18 4,18" fill={tier.gemColors[2]} opacity="0.9"/>
                    {/* Crown top-right facet */}
                    <polygon points="30,3 46,3 56,18 38,18" fill={tier.gemColors[0]} opacity="0.85"/>
                    {/* Crown center facet */}
                    <polygon points="14,3 46,3 38,18 22,18" fill={tier.gemColors[0]}/>
                    {/* Pavilion left */}
                    <polygon points="4,18 22,18 30,49" fill={tier.gemColors[1]} opacity="0.8"/>
                    {/* Pavilion center */}
                    <polygon points="22,18 38,18 30,49" fill={tier.gemColors[0]} opacity="0.95"/>
                    {/* Pavilion right */}
                    <polygon points="38,18 56,18 30,49" fill={tier.gemColors[1]} opacity="0.7"/>
                    {/* Outline */}
                    <polygon points="14,3 46,3 56,18 30,49 4,18" fill="none" stroke={tier.gemColors[0]} strokeWidth="1.2" opacity="0.6"/>
                    {/* Inner facet lines */}
                    <line x1="22" y1="18" x2="30" y2="49" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"/>
                    <line x1="38" y1="18" x2="30" y2="49" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"/>
                    <line x1="4" y1="18" x2="56" y2="18" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8"/>
                  </svg>
                </div>
                <h3 style={{ color: tier.color }}>{tier.name}</h3>
                <p>{tier.desc}</p>
                <ul>
                  {tier.perks.map(p => <li key={p}>✓ {p}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="rewards-cta">
            <button className="btn-primary" onClick={() => navigate('faq')}>Learn More About Rewards</button>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-section">
        <div className="cta-bg">
          <div className="cta-orb" />
        </div>
        <div className="container cta-content">
          <h2>Ready to Elevate<br/>Your Night Out?</h2>
          <p>your all-in-one nightlife experience starts here.</p>
          <div className="cta-buttons">
            <a href="https://apps.apple.com/au/app/hiveeclub/id1584271794" target="_blank" rel="noopener noreferrer" className="store-btn apple">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              <div><small>Download on the</small><strong>App Store</strong></div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.hivee.hivee" target="_blank" rel="noopener noreferrer" className="store-btn google">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c.33.18.7.24 1.08.17l12.29-7.08-2.6-2.6-10.77 9.51zm-1.06-1.38V1.62L13.64 12 2.12 22.38zm17.3-11.43L16.8 9.4 14 12l2.8 2.8 2.66-1.54a1.5 1.5 0 0 0 0-2.52zM4.26.07L16.55 7.15 13.95 9.75 3.18.24A1.5 1.5 0 0 0 4.26.07z"/></svg>
              <div><small>Get it on</small><strong>Google Play</strong></div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
